import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import moment from 'moment';

import { useNotif } from 'contexts/NotifContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import {
  formatListFieldUser,
  getTabObjective,
  objectiveCorrespondance
} from 'utils/functions';
import Icon from 'utils/icons';
import pluralize from 'utils/pluralize';
import { ACTIVATION, FUNNEL, GROUP } from 'utils/rest';

import ConnectorsListField from 'components/ConnectorsListField';
import {
  HelpTooltip,
  Text,
  Tooltip,
  WarningMessage
} from 'components/DataDisplay';
import { FormSection } from 'components/FormSection';
import {
  AutocompleteTextField,
  Button,
  FunnelCalendar,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item } from 'components/Layout';
import { ListField } from 'components/ListField';

import { StatusText } from './components/StatusText';
import { ModalDelete } from './ModalDelete';

const getPaid = campaign => {
  if (campaign.isCampaignGroup) {
    if (campaign.paid === '') {
      return 'none';
    }
    return campaign.paid;
  }
  if (campaign.paid) {
    return 'true';
  }
  return 'false';
};

const translatedReportStatus = {
  toVerify: 'A vérifier',
  validated: 'Validé'
};

// Status select options (kept identical to the legacy modal) — group vs simple
const groupStatusOptions = [
  { value: 'groupDraft', label: 'Brouillon' },
  { value: 'validated', label: 'Validée' },
  { value: 'paused', label: 'Pause' },
  { value: 'canceled', label: 'Annulée' },
  { value: 'completed', label: 'Terminée' },
  { value: 'active', label: 'Activée' },
  { value: 'invoicePending', label: 'Paiement en attente' },
  { value: 'invoiceDone', label: 'Paiement en cours' }
];
const simpleStatusOptions = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'validated', label: 'Validée' },
  { value: 'paused', label: 'Pause' },
  { value: 'canceled', label: 'Annulée' },
  { value: 'completed', label: 'Terminée' },
  { value: 'active', label: 'Activée' },
  { value: 'ready', label: 'Prête' },
  { value: 'invoicePending', label: 'Paiement en attente' },
  { value: 'invoiceDone', label: 'Paiement en cours' }
];

// Paiement select options — labels are intentionally inverted (do not "fix")
const orderStatusPaidOptions = [
  { value: 'paid', label: 'Non payée' },
  { value: 'unpaid', label: 'Payée' },
  { value: 'pending', label: 'En attente' },
  { value: 'rejected', label: 'Rejetée' }
];
const groupPaidOptions = [
  { value: 'none', label: 'Non payée' },
  { value: 'partial', label: 'Partiel' },
  { value: 'paid', label: 'Payée' }
];
const simplePaidOptions = [
  { value: 'false', label: 'Non Payée' },
  { value: 'true', label: 'Payée' }
];

const ModalGestion = props => {
  const {
    campaign,
    onValidate,
    onDelete,
    config,
    setupPendingTooltip,
    datas
  } = props;
  const { notify } = useNotif();

  const [loaded, setLoaded] = useState(false);
  const { themeColors } = useTheme();
  const {
    ink,
    inkSoft,
    muted,
    faint,
    line,
    green,
    blue,
    red
  } = themeColors.designSystem;
  const searchOptionsKeys = ['name', 'id'];
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [isCampaignSetupPending, setIsCampaignSetupPending] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersSelected, setUsersSelected] = useState([]);
  const [name, setName] = useState(campaign.name);
  const [disableDataFeedback, setDisableDataFeedback] = useState(false);
  const [shop, setShop] = useState([]);
  const [status, setStatus] = useState(campaign.status);
  const [paid, setPaid] = useState(getPaid(campaign));
  const [funnelId, setFunnelId] = useState(campaign.funnelId || '');
  const [organisation, setOrganisation] = useState('');
  const [emailRecipient, setEmailRecipient] = useState(campaign.ownerEmail);
  const [campaignReport, setCampaignReport] = useState(null);
  const [nameReport, setNameReport] = useState(campaignReport?.name);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [split, setSplit] = useState(campaign?.split || false);
  const [tags, setTags] = useState([]);
  const [objective, setObjective] = useState(campaign.objective);
  const [startDate, setStartDate] = useState(
    moment(campaign.start)
      .utc()
      .startOf('day')
      .format()
  );
  const [endDate, setEndDate] = useState(
    moment(campaign.end)
      .utc()
      .endOf('day')
      .format()
  );
  const [errorName, setErrorName] = useState(false);
  const [errorNameReport, setErrorNameReport] = useState(false);
  const [connectors, setConnectors] = useState(
    campaign.isCampaignGroup
      ? campaign.campaignConfigs[0]?.connectors || []
      : campaign.connectors || []
  );
  const [brdStatus, setBrdStatus] = useState('Default');
  const [funnelStatus, setFunnelStatus] = useState('Default');
  const [sending, setSending] = useState(false);

  const { user } = useUser();

  const campaignId = campaign.id || campaign._id;

  const formatStartDate = date => {
    return date
      ? moment(date)
          .utc()
          .startOf('day')
          .format()
      : null;
  };

  const formatEndDate = date => {
    return date
      ? moment(date)
          .utc()
          .endOf('day')
          .format()
      : null;
  };

  const setPeriod = async ({ startDate: start, endDate: end }) => {
    setStartDate(formatStartDate(start));
    setEndDate(formatEndDate(end));
  };

  const onAutoCompliteChange = (data, reason) => {
    if (reason === 'select-option' || reason === 'selectOption') {
      setFunnelId(data?.id);
    }

    if (reason === 'clear') {
      setFunnelId('');
    }
  };

  const setupConnectors = async () => {
    if (funnelId) {
      const id = campaign.id || campaign._id;
      if (funnelId !== campaign.funnelId) {
        await ACTIVATION.updateCampaignConfig(id, {
          funnelId
        });
      }
      const res = await ACTIVATION.setupConnectors([id]);
      if (res === null) setFunnelStatus('Error');
      else if (res?.connectorsConfigArr.length === 0) setFunnelStatus('Empty');
      else if (res?.connectorsConfigArr.length > 0) setFunnelStatus('Success');
    }
  };

  const setupDataFeedback = async () => {
    if (funnelId) {
      setDisableDataFeedback(true);
      const id = campaign.id || campaign._id;
      if (funnelId !== campaign.funnelId) {
        await ACTIVATION.updateCampaignConfig(id, {
          funnelId
        });
      }
      const res = await FUNNEL.setupDataFeedback(id);
      if (res === '') setBrdStatus('Success');
      else setBrdStatus('Error');
      setDisableDataFeedback(false);
    }
  };

  const formatPaid = statusPaid => {
    if (campaign.isCampaignGroup) {
      return statusPaid;
    }
    if (statusPaid === 'false') {
      return false;
    }
    return true;
  };

  const findAccount = (key, platformAccounts) => {
    if (key === 'facebook' || key === 'instagram') {
      return platformAccounts.find(({ model }) => model === 'facebookAds');
    }
    if (key === 'youtube') {
      return platformAccounts.find(
        platformaccount => platformaccount.model === 'googleAds'
      );
    }
    if (key === 'sms') {
      return platformAccounts.find(
        platformaccount => platformaccount.model === 'spotHit'
      );
    }
    return platformAccounts.find(
      platformaccount => platformaccount.model === key
    );
  };

  const getPlatformFromCampaign = async el => {
    const platforms = [];
    const group = await GROUP.getGroup(el.groupId);
    const mediaOrders =
      (await ACTIVATION.getMediaOrdersByCampaignId(el._id)) || [];
    const uniqueActivableMedia = mediaOrders.reduce((acc, item) => {
      if (item.activableMedia) {
        acc.add(item.activableMedia);
      }
      return acc;
    }, new Set());
    if (group) {
      for (const key of uniqueActivableMedia) {
        const foundAccount = findAccount(key, group.platformAccounts);
        if (foundAccount) {
          platforms.push({
            config: foundAccount,
            media: key,
            type: 'account'
          });
        }
        const foundPage = group.platformPages.find(page => page.media === key);
        if (foundPage) {
          platforms.push({
            config: foundPage,
            media: key,
            type: 'page'
          });
        }
      }
    }
    return platforms;
  };

  const handleChangeConnectors = newConfig => {
    setConnectors(newConfig);
  };

  const getCampaignReport = async campaignReportSlideId => {
    const report = await ACTIVATION.getCampaignReportById(
      campaignReportSlideId
    );
    setNameReport(report.name);
    setEmailRecipient(report?.sendTo || campaign.ownerEmail);
    setCampaignReport(report);
  };

  const updateCampaignReport = async campaignReportData => {
    const updatedReport = await ACTIVATION.updateCampaignReport(
      campaignReport.id,
      { ...campaignReportData, sendTo: emailRecipient }
    );
    setCampaignReport(updatedReport);
    return updatedReport;
  };

  const validate = async () => {
    let error = false;
    if (name === '') {
      error = true;
      setErrorName(true);
    }

    if (nameReport === '') {
      error = true;
      setErrorNameReport(true);
    }

    const olfFunnelId = campaign.funnelId ? campaign.funnelId : '';
    const objectSend = {
      ...(name !== campaign.name ? { name } : {}),
      ...(formatPaid(paid) !== campaign.paid ? { paid: formatPaid(paid) } : {}),
      ...(funnelId !== olfFunnelId ? { funnelId } : {}),
      ...(status !== campaign.status ? { status } : {}),
      ...(split !== campaign.split ? { split } : {}),
      ...(startDate !==
      moment(campaign.start)
        .utc()
        .startOf('day')
        .format()
        ? { start: startDate }
        : {}),
      ...(endDate !==
      moment(campaign.end)
        .utc()
        .endOf('day')
        .format()
        ? { end: endDate }
        : {}),
      userIds: usersSelected.map(el => el._id),
      tags,
      applyTags: true
    };
    if (
      (campaign.status === 'draft' || campaign.status === 'groupDraft') &&
      objectSend.status === 'validated'
    ) {
      if (!campaign.isCampaignGroup) {
        if (campaign.platforms.length === 0) {
          const platforms = await getPlatformFromCampaign(campaign);
          objectSend.platforms = platforms;
        }
      } else {
        for await (const id of campaign.campaignConfigIds) {
          const currentConfig = await ACTIVATION.getCampaignConfigById(id);
          if (currentConfig.platforms.length === 0) {
            const platforms = await getPlatformFromCampaign(currentConfig);
            await ACTIVATION.updateCampaignConfig(id, { platforms });
          }
        }
      }
    }
    if (objective) {
      objectSend.objective = objective;
    }

    objectSend.connectors = connectors;
    if (!error) {
      if (
        campaignReport &&
        (nameReport !== campaignReport?.name ||
          emailRecipient !== campaignReport?.sendTo)
      ) {
        await updateCampaignReport({
          name: nameReport,
          sendTo: emailRecipient
        });
      }

      onValidate(objectSend);
    }
  };

  const getShops = async shopIds => {
    const allShop = await ACTIVATION.getActivationShops();
    const shops = allShop.filter(el => shopIds.includes(el._id));
    setShop(shops.map(el => el.name));
  };

  const getShopGroup = async shopGroupId => {
    const group = await ACTIVATION.getActivationShopGoupById(shopGroupId);
    setOrganisation(group?.name || '');
  };

  const getTagsCampaignChild = campaignGroup => {
    const newTags = campaignGroup.campaignConfigs.reduce(
      (acc, el) => [...new Set([...acc, ...el.tags])],
      []
    );
    return newTags;
  };

  const getOrganizationUsers = (users, userIds = []) => {
    return users
      .filter(currentUser => userIds.includes(currentUser._id))
      .map(filteredUser => ({
        id: filteredUser._id,
        value: filteredUser.email,
        label: filteredUser.email
      }));
  };

  const openUrl = url => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const disabledPayementStatus = [
    'draft',
    'groupDraft',
    'invoicePending',
    'invoiceDone'
  ];
  const canDeleteCampaign = disabledPayementStatus.includes(campaign.status);
  const deleteButtonColor = canDeleteCampaign ? red : muted;

  useEffect(() => {
    if (campaign) {
      const group = config.groups.find(el => el._id === campaign.groupId);
      setOrganisation(group?.name || 'Unkwnown');
      if (campaign.isCampaignGroup && campaign.shopIds) {
        getShops(campaign.shopIds);
      }
      if (campaign.isCampaignGroup && campaign.shopGroupId) {
        getShopGroup(campaign?.shopGroupId || '');
      }
      if (campaign.campaignReportSlideId) {
        getCampaignReport(campaign.campaignReportSlideId);
      }
      const newTags = campaign.isCampaignGroup
        ? getTagsCampaignChild(campaign)
        : campaign?.tags;

      const userList = config.users.reduce((accUser, currentUser) => {
        if (
          currentUser._id !== campaign.owner &&
          campaign.userIds.includes(currentUser._id)
        ) {
          accUser.push(formatListFieldUser(currentUser));
        }
        return accUser;
      }, []);

      setTags([...newTags]);
      setUsersSelected(userList);
      setAllUsers(config.users.map(formatListFieldUser));
      setOrganizationUsers(
        getOrganizationUsers(config?.users, [
          ...(group?.userIds || []),
          ...(campaign?.userIds || []),
          ...(campaign?.ownerId ? [campaign.ownerId] : [])
        ])
      );
      setLoaded(true);
    }
    // eslint-disable-next-line
  }, [campaign, config.groups, config.users]);

  const fetchData = async () => {
    const lastSetupTasks = await ACTIVATION.getCampaignLastSetupTasks(
      campaign?.campaignConfigIds?.length
        ? campaign.campaignConfigIds
        : [campaignId]
    );
    const isPending = lastSetupTasks.some(
      setupTask => !['done', 'error', 'aborted'].includes(setupTask.status)
    );
    setIsCampaignSetupPending(isPending);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const sendEmailRapport = async campaignConfigIds => {
    const res = await ACTIVATION.sendEmailCampaignReportSlide(
      campaignConfigIds
    );
    if (!res) {
      return notify({
        message: "Echec de l'envoi",
        variant: 'error'
      });
    }
    await getCampaignReport(campaign.campaignReportSlideId);
    return notify({
      message: 'Rapport envoyé',
      variant: 'success'
    });
  };

  const isCampaignReportVerified = campaignReport?.status
    ? campaignReport?.status === 'toVerify'
    : true;
  const isCampaignReportSent = !!campaignReport?.lastSend;

  // Meta pairs displayed as a single wrapping row at the top of "Général"
  const metaPairs = campaign.isCampaignGroup
    ? [
        {
          key: 'Type de campagne :',
          value: 'Multicampagne'
        },
        {
          key: 'Objectif de campagne :',
          value: objectiveCorrespondance(campaign.objective)
        },
        {
          key: 'Groupement de magasin :',
          value: organisation
        },
        {
          key: 'Magasins :',
          value: `${shop.length} ${pluralize('magasin', shop)}`,
          tooltip: shop.join(', ')
        }
      ]
    : [
        {
          key: 'Type de campagne :',
          value: 'Simple'
        },
        {
          key: 'Organisation :',
          value: organisation
        },
        {
          key: 'Objectif de campagne :',
          value: objectiveCorrespondance(campaign.objective)
        }
      ];

  // shared label styling for hand-built field labels (no native title prop)
  const fieldLabel = (label, required) => (
    <Text color={inkSoft} fontWeight={600} fontSize="11px">
      {label}
      {required && (
        <Text component="span" color={red} fontWeight={600}>
          {' '}
          *
        </Text>
      )}
    </Text>
  );

  const divider = (
    <Item
      flex
      style={{
        width: '100%',
        height: '1px',
        backgroundColor: line,
        margin: '14px 0'
      }}
    />
  );

  return (
    <Container>
      {/* Header */}
      <Item
        flex
        justify="flex-start"
        style={{ width: '100%', marginBottom: '14px' }}
      >
        <Text component="span" color={ink} fontSize="16px" fontWeight={700}>
          {name}
        </Text>
        <Text
          component="span"
          color={faint}
          fontSize="13px"
          style={{ marginLeft: '6px' }}
        >
          ({campaignId})
        </Text>
      </Item>

      {/* ---- Général ---- */}
      <FormSection title="Général">
        <Container justify="flex-start">
          {/* meta as a single flex-wrap row */}
          <Item
            flex
            justify="flex-start"
            style={{
              width: '100%',
              flexWrap: 'wrap',
              gap: '8px 30px',
              marginBottom: '14px'
            }}
          >
            {metaPairs.map(pair => (
              <Item
                key={pair.key}
                flex
                justify="flex-start"
                style={{ width: 'auto', gap: '5px' }}
              >
                <Text
                  component="span"
                  color={muted}
                  fontWeight={600}
                  fontSize="12.5px"
                >
                  {pair.key}
                </Text>
                <Text
                  component="span"
                  color={ink}
                  fontWeight={700}
                  fontSize="12.5px"
                >
                  {pair.value}
                </Text>
                {pair.tooltip !== undefined && (
                  <HelpTooltip
                    title={pair.tooltip}
                    style={{ backgroundColor: themeColors.black }}
                  />
                )}
              </Item>
            ))}
          </Item>
          <Item
            flex
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '13px 20px',
              width: '100%'
            }}
          >
            <TextField
              disabled={isCampaignSetupPending}
              title="Nom"
              required
              onChange={e => {
                if (e !== '') {
                  setErrorName(false);
                }
                setName(e);
              }}
              small
              error={errorName}
              value={name}
            />
            {user.roles.includes('ADMIN') && (
              <Select
                title="Objectif de campagne"
                value={objective}
                options={getTabObjective.map(el => ({
                  value: el.key,
                  label: el.value
                }))}
                onChange={e => {
                  setObjective(e);
                }}
              />
            )}
            <Item
              flex
              style={{
                flexDirection: 'column',
                gap: '5px',
                width: '100%',
                alignItems: 'flex-start'
              }}
            >
              {fieldLabel('Dates', true)}
              <FunnelCalendar
                isPending={isCampaignSetupPending}
                disableCampare
                start={startDate}
                end={endDate}
                onChange={setPeriod}
                displayDate
                onClearDate={() => {
                  setPeriod({ reset: true });
                }}
                displayClearDate={false}
                inline
              />
            </Item>
            <Select
              disabled={isCampaignSetupPending}
              title="Statut"
              required
              options={
                campaign.isCampaignGroup
                  ? groupStatusOptions
                  : simpleStatusOptions
              }
              value={status}
              onChange={e => {
                setStatus(e);
              }}
            />
            {campaign.orderStatus ? (
              <Select
                title="Paiement"
                required
                options={orderStatusPaidOptions}
                value={campaign.orderStatus}
                disabled
              />
            ) : (
              <Select
                title="Paiement"
                required
                options={
                  campaign.isCampaignGroup
                    ? groupPaidOptions
                    : simplePaidOptions
                }
                value={paid}
                disabled={
                  disabledPayementStatus.includes(status) ||
                  isCampaignSetupPending
                }
                onChange={e => {
                  setPaid(e);
                }}
              />
            )}
            <Item
              flex
              style={{
                flexDirection: 'column',
                gap: '5px',
                width: '100%',
                alignItems: 'flex-start'
              }}
            >
              {fieldLabel('Split de la campagne')}
              <Item flex style={{ alignItems: 'center' }}>
                <Switch
                  disabled={isCampaignSetupPending}
                  checked={split}
                  onChange={() => {
                    setSplit(!split);
                  }}
                  options={['Non', 'Oui']}
                />
              </Item>
            </Item>
          </Item>

          {/* delete button row */}
          <Item
            flex
            justify="flex-start"
            style={{ width: '100%', gap: '10px', marginTop: '14px' }}
          >
            <Button
              disabled={!canDeleteCampaign}
              disabledMessage="Une campagne ne peut être supprimée qu'en brouillon, ou tant que son paiement n'est pas finalisé."
              variant="outlined"
              color={deleteButtonColor}
              size="small"
              style={{ backgroundColor: themeColors.keep_light }}
              onClick={() => {
                setOpenModalDelete(true);
              }}
            >
              <Icon icon="trash" attrs={{ fill: deleteButtonColor }} />
              <Text color="inherit" style={{ marginLeft: '5px' }}>
                Supprimer la campagne
              </Text>
            </Button>
          </Item>
          {divider}
        </Container>
      </FormSection>

      {/* ---- Tags ---- */}
      <FormSection title="Tags">
        <Container justify="flex-start">
          <Text fontSize="11.5px" color={muted} style={{ lineHeight: 1.5 }}>
            Sélectionnez des tags pour votre campagne, ou écrivez-en un nouveau
            puis appuyez sur <strong>Entrée</strong>. Pour une multicampagne,
            les tags seront copiés à l’ensemble de ses campagnes enfants.
          </Text>
          <Item
            flex
            justify="flex-start"
            style={{ width: '100%', marginTop: '11px' }}
          >
            {loaded && (
              <ListField
                disabled={isCampaignSetupPending}
                options={config.tags.sort().map(el => ({ tag: el }))}
                buttonBgColor={themeColors.keep_light}
                buttonTextColor={inkSoft}
                buttonBorderColor={line}
                values={tags.map(el => ({ tag: el }))}
                onChange={e => {
                  setTags(e.map(({ tag }) => tag));
                }}
                searchOptionsKeys={['tag']}
                actionTxt="Ajouter un tag"
                deleteAction
                keyEnter
                tagChips
              />
            )}
          </Item>
          {divider}
        </Container>
      </FormSection>

      {/* ---- Funnel ---- */}
      <FormSection title="Funnel">
        <Container justify="flex-start">
          <Item
            flex
            justify="flex-start"
            style={{ width: '100%', maxWidth: '380px', marginBottom: '11px' }}
          >
            <AutocompleteTextField
              options={config.funnels}
              formatLabel={option => {
                let str = option[searchOptionsKeys[0]];
                if (searchOptionsKeys[1]) {
                  str += ` (${option[searchOptionsKeys[1]]})`;
                }
                return str;
              }}
              small
              value={config.funnels.find(el => el.id === funnelId)}
              onChange={onAutoCompliteChange}
              placeholder="Funnel leads"
              disabled={
                disabledPayementStatus.includes(status) ||
                isCampaignSetupPending
              }
            />
          </Item>
          <Text fontSize="11.5px" color={muted} style={{ lineHeight: 1.5 }}>
            Sélectionnez le funnel que vous souhaitez relier à la campagne et
            lancer vos setup. Par défaut, le funnel créé automatiquement lors de
            la validation de la campagne est lié.
          </Text>
          <Item
            flex
            justify="flex-start"
            style={{ width: '100%', gap: '10px', marginTop: '14px' }}
          >
            <Tooltip title="Vous devez avoir un funnel lié à votre campagne pour pouvoir setuper les connecteurs.">
              <Button
                disabled={!funnelId || isCampaignSetupPending}
                variant="contained"
                color={blue}
                size="small"
                onClick={setupConnectors}
              >
                <SettingsInputComponentIcon style={{ fontSize: '16px' }} />
                <Text color="inherit" style={{ marginLeft: '5px' }}>
                  Setuper les connecteurs
                </Text>
              </Button>
              <StatusText status={funnelStatus} item="connectors" />
            </Tooltip>
            <Tooltip title="Vous devez avoir exporté votre audience ou avoir setupé automatiquement votre campagne avant de pouvoir créer sa BRD.">
              <Button
                disabled={
                  !funnelId || disableDataFeedback || isCampaignSetupPending
                }
                variant="contained"
                color={blue}
                size="small"
                onClick={setupDataFeedback}
              >
                <RefreshIcon style={{ fontSize: '16px' }} />
                <Text color="inherit" style={{ marginLeft: '5px' }}>
                  Setuper la boucle de retour data
                </Text>
              </Button>
              <StatusText status={brdStatus} item="brd" />
            </Tooltip>
          </Item>
          {divider}
        </Container>
      </FormSection>

      {/* ---- Connecteur analytics ---- */}
      <FormSection title="Connecteur analytics">
        <Container justify="flex-start">
          <Text
            fontSize="11.5px"
            color={muted}
            style={{ lineHeight: 1.5, marginBottom: '9px' }}
          >
            Faites remonter des données analytics comme résultat de campagne en
            KPI 2/3/4 ici.
          </Text>
          <WarningMessage title="Attention :">
            pour croiser ces données avec l&apos;audience de votre campagne, une
            boucle de retour data setupée est nécessaire.
          </WarningMessage>
          <Item
            flex
            justify="flex-start"
            style={{ width: '100%', marginTop: '11px' }}
          >
            <ConnectorsListField
              AnalyticsOnly
              value={connectors}
              onChange={handleChangeConnectors}
              importModel={datas.importModel}
              configGenerators={datas.configGenerators}
              configConnexion={datas.configConnexion}
            />
          </Item>
          {divider}
        </Container>
      </FormSection>

      {/* ---- Rapport ---- */}
      {campaignReport && (
        <FormSection title="Rapport">
          <Container justify="flex-start">
            <Item
              flex
              justify="flex-start"
              style={{ width: '100%', gap: '5px' }}
            >
              <Text
                component="span"
                color={muted}
                fontWeight={600}
                fontSize="12.5px"
              >
                Statut :
              </Text>
              <Text
                component="span"
                color={ink}
                fontWeight={700}
                fontSize="12.5px"
              >
                {translatedReportStatus[campaignReport?.status]}
              </Text>
            </Item>
            <Item
              flex
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '13px 20px',
                width: '100%',
                marginTop: '11px'
              }}
            >
              <TextField
                disabled={isCampaignSetupPending}
                title="Nom"
                required
                onChange={e => {
                  if (e !== '') {
                    setErrorNameReport(false);
                  }
                  setNameReport(e);
                }}
                small
                error={errorNameReport}
                value={nameReport}
              />
              <Select
                title="Envoyer à"
                required
                options={organizationUsers}
                value={emailRecipient}
                disabled={isCampaignReportSent}
                onChange={e => {
                  setEmailRecipient(e);
                }}
              />
            </Item>
            <Item
              flex
              justify="flex-start"
              style={{ gap: '10px', marginTop: '14px' }}
            >
              <Button
                disabled={!campaignReport?.url}
                variant="contained"
                color={blue}
                size="small"
                onClick={() => openUrl(campaignReport?.url)}
              >
                <LaunchIcon style={{ fontSize: '16px' }} />
                <Text color="inherit" style={{ marginLeft: '5px' }}>
                  Ouvrir le rapport
                </Text>
              </Button>
              <Button
                variant="contained"
                color={green}
                size="small"
                onClick={() =>
                  updateCampaignReport({
                    status: isCampaignReportVerified ? 'validated' : 'toVerify'
                  })
                }
              >
                <Text color="inherit">
                  {isCampaignReportVerified ? 'Valider' : 'Modifier'}
                </Text>
              </Button>
              <Button
                disabled={
                  sending || isCampaignReportVerified || isCampaignReportSent
                }
                variant="contained"
                color={green}
                size="small"
                loading={sending}
                onClick={async () => {
                  setSending(true);
                  await sendEmailRapport([campaignId]);
                  setSending(false);
                }}
              >
                <SendIcon style={{ fontSize: '16px' }} />
                <Text color="inherit" style={{ marginLeft: '5px' }}>
                  Envoyer
                </Text>
              </Button>
            </Item>
            <Item
              flex
              justify="flex-start"
              style={{ width: '100%', marginTop: '11px' }}
            >
              <Text fontSize="12px" color={muted}>
                {/* "Il s'agit de votre premier envoi. Le destinataire va recevoir un email pour accéder au document" */}
                {!campaignReport?.lastSend
                  ? 'Dernier envoi : -'
                  : `Dernier envoi : ${moment(campaignReport.lastSend).format(
                      'L - LT'
                    )}`}
              </Text>
            </Item>
            {(campaign.isCampaignGroup || campaign.campaignGroupId) && (
              <Item
                flex
                justify="flex-start"
                alignItems="center"
                style={{ width: '100%', gap: '10px', marginTop: '11px' }}
              >
                <Text fontSize="12px" color={muted}>
                  Copier le contenu dans les autres campagnes enfants
                </Text>
                <Switch
                  checked={split}
                  onChange={() => {}}
                  options={['Non', 'Oui']}
                />
              </Item>
            )}
            {divider}
          </Container>
        </FormSection>
      )}

      {/* ---- Accès à la campagne ---- */}
      <FormSection title="Accès à la campagne">
        <Container justify="flex-start">
          <Item flex justify="flex-start" style={{ width: '100%' }}>
            {loaded && (
              <ListField
                options={allUsers}
                searchOptionsKeys={['email']}
                values={usersSelected}
                onChange={setUsersSelected}
                actionTxt="Ajouter"
                hideKeys={['_id']}
                deleteAction
                disabled={isCampaignSetupPending}
                buttonBgColor={themeColors.keep_light}
                buttonTextColor={inkSoft}
                buttonBorderColor={line}
                cardRows
              />
            )}
          </Item>
        </Container>
      </FormSection>
      {openModalDelete && (
        <ModalDelete
          open
          onClose={() => {
            setOpenModalDelete(false);
          }}
          onDelete={() => {
            setOpenModalDelete(false);
            onDelete();
          }}
        />
      )}

      {/* Footer */}
      <Item flex style={{ gap: '10px', marginTop: '20px', width: '100%' }}>
        <Item flex style={{ flex: 1 }} />
        <Tooltip title={setupPendingTooltip} disabled={!isCampaignSetupPending}>
          <Button
            disabled={isCampaignSetupPending}
            variant="contained"
            size="medium"
            color={ink}
            onClick={() => {
              validate();
            }}
          >
            <Text
              color={themeColors.keep_light}
              fontSize="12.5px"
              style={{ marginRight: '5px' }}
            >
              Valider
            </Text>
            <Icon icon="right" />
          </Button>
        </Tooltip>
      </Item>
    </Container>
  );
};

ModalGestion.defaultProps = {
  funnels: [],
  config: {
    funnels: [],
    users: [],
    groups: [],
    tags: []
  },
  onValidate: () => {},
  onDelete: () => {}
};

ModalGestion.propTypes = {
  config: PropTypes.shape({
    funnels: PropTypes.arrayOf(PropTypes.shape()),
    users: PropTypes.arrayOf(PropTypes.shape()),
    groups: PropTypes.arrayOf(PropTypes.shape()),
    tags: PropTypes.arrayOf(PropTypes.string)
  }),
  campaign: PropTypes.shape().isRequired,
  funnels: PropTypes.arrayOf(PropTypes.shape()),
  onValidate: PropTypes.func,
  onDelete: PropTypes.func
};

export default ModalGestion;
